export interface ProviderSummary {
  id: string;
  name: string;
  category: string;
  siteUrl: string;
  state: "ok" | "auth" | "offline";
  logo?: string;
  capabilities: { list: boolean; search: boolean; detail: boolean; stream: boolean };
}

export interface VideoSummary {
  id: string;
  title: string;
  thumbnail?: string;
  /** Signed worker URL for the thumbnail (falls back to the upstream URL). */
  image?: string;
  duration?: string;
  provider: string;
  url?: string;
  quality?: string;
  date?: string;
}

export interface VideoDetail extends VideoSummary {
  description?: string;
  tags?: string[];
  models?: { name: string; thumbnail?: string }[];
  views?: string;
}

export interface MediaRef {
  url: string;
  type: "mp4" | "hls" | "dash";
  quality?: string;
  headers?: Record<string, string>;
  /** Worker URL that streams this media (present when the API signs URLs). */
  playUrl?: string;
  /** Direct worker route that resolves and streams this rendition. */
  playRoute?: string;
}

let apiBase = "/api";
let apiOrigin = "";

export async function configureApi(): Promise<void> {
  try {
    const response = await fetch("/config.json", { cache: "no-store" });
    if (!response.ok) return;
    const config = (await response.json()) as { apiBase?: string };
    if (config.apiBase) {
      apiBase = config.apiBase.replace(/\/$/, "");
      apiOrigin = apiBase.replace(/\/api$/, "");
    }
  } catch {
    /* same-origin fallback stays in place */
  }
}

/** An API failure that still carries what the caller needs to offer a way out. */
export class ApiError extends Error {
  readonly embeds: string[];
  constructor(message: string, embeds: string[] = []) {
    super(message);
    this.name = "ApiError";
    this.embeds = embeds;
  }
}

async function request<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, { signal });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; embeds?: unknown };
    const embeds = Array.isArray(body.embeds)
      ? body.embeds.filter((entry): entry is string => typeof entry === "string")
      : [];
    throw new ApiError(body.message ?? `Request failed (${response.status})`, embeds);
  }
  return (await response.json()) as T;
}

export async function fetchProviders(signal?: AbortSignal): Promise<ProviderSummary[]> {
  const payload = await request<{ providers: ProviderSummary[] }>("/providers", signal);
  return payload.providers;
}

export async function fetchListing(
  providerId: string,
  options: { page?: number; sort?: string; query?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<VideoSummary[]> {
  const params = new URLSearchParams();
  if (options.page) params.set("page", String(options.page));
  if (options.sort) params.set("sort", options.sort);
  if (options.query) params.set("q", options.query);
  if (options.limit) params.set("limit", String(options.limit));
  const payload = await request<{ items: VideoSummary[] }>(
    `/providers/${encodeURIComponent(providerId)}/list?${params.toString()}`,
    signal,
  );
  return payload.items;
}

export async function fetchDetail(
  providerId: string,
  videoId: string,
  signal?: AbortSignal,
): Promise<VideoDetail> {
  const payload = await request<{ detail: VideoDetail }>(
    `/providers/${encodeURIComponent(providerId)}/detail/${encodeURIComponent(videoId)}`,
    signal,
  );
  return payload.detail;
}

export async function fetchStream(
  providerId: string,
  videoId: string,
  signal?: AbortSignal,
): Promise<MediaRef[]> {
  const payload = await request<{ media: MediaRef[] }>(
    `/providers/${encodeURIComponent(providerId)}/stream/${encodeURIComponent(videoId)}`,
    signal,
  );
  return payload.media;
}

/**
 * Search one provider through its own listing endpoint: `q` is forwarded to the
 * provider engine, which either filters its catalogue or falls back to the
 * newest page for providers without a search path.
 */
export async function searchProvider(
  providerId: string,
  query: string,
  limit = 36,
): Promise<VideoSummary[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const payload = await request<{ items: VideoSummary[] }>(
    `/providers/${encodeURIComponent(providerId)}/list?${params.toString()}`,
  );
  return payload.items;
}

export interface SearchGroup {
  provider: string;
  name: string;
  items: VideoSummary[];
  error?: string;
}

export async function searchAll(
  query: string,
  providerIds: string[],
  deadlineMs?: number,
): Promise<SearchGroup[]> {
  const params = new URLSearchParams({ q: query, limit: "18" });
  if (providerIds.length > 0) params.set("providers", providerIds.join(","));
  if (deadlineMs) params.set("deadline", String(deadlineMs));
  const payload = await request<{ results: SearchGroup[] }>(`/search?${params.toString()}`);
  // Plenty of sites answer any query with their newest page. A result whose
  // title shares no word with the query is that page, not a match, and showing
  // it is what made nonsense queries return hundreds of "results".
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .filter((term) => term.length >= 2);
  if (terms.length === 0) return payload.results;
  return payload.results.map((group) => ({
    ...group,
    items: (group.items ?? []).filter((item) => {
      const title = (item.title ?? "").toLowerCase();
      return terms.some((term) => title.includes(term));
    }),
  }));
}

/** Rewrite an upstream media url so the browser streams it through the worker. */
export function proxyUrl(media: MediaRef, providerSite?: string): string {
  if (media.playUrl) {
    const path = media.playUrl.startsWith("/") ? media.playUrl : `/${media.playUrl}`;
    return apiOrigin ? `${apiOrigin}${path}` : path;
  }
  const params = new URLSearchParams({ u: media.url });
  const referer = media.headers?.["Referer"] ?? media.headers?.["referer"] ?? providerSite;
  if (referer) params.set("ref", referer);
  const extra = Object.entries(media.headers ?? {}).filter(
    ([key]) => key.toLowerCase() !== "referer",
  );
  if (extra.length > 0) params.set("h", JSON.stringify(Object.fromEntries(extra)));
  return `${apiBase}/proxy?${params.toString()}`;
}
